import { analyticsEventAllowedProperties } from './analyticsEvents'
import type {
  AnalyticsEventName,
  AnalyticsEventProperties,
  AnalyticsProperties,
  AnalyticsPropertyValue,
} from './analyticsTypes'

const maximumStringLength = 120
const maximumArrayItems = 20
const safeSensitiveAnalyticsPropertyNameExceptions = ['email_domain']

export const sensitiveAnalyticsPropertyNames = [
  'address',
  'agentCpf',
  'cnpj',
  'coordinate',
  'coordinates',
  'corporateName',
  'cpf',
  'document',
  'email',
  'fullName',
  'latitude',
  'legalEntityName',
  'longitude',
  'message',
  'name',
  'phone',
  'prompt',
  'raw',
  'search',
  'streetAddress',
  'text',
  'tradeName',
]

const sensitivePropertyNamePattern = new RegExp(
  sensitiveAnalyticsPropertyNames
    .map(propertyName => propertyName.replace(/[A-Z]/g, match => `[_-]?${match.toLowerCase()}`))
    .join('|'),
  'i',
)

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const cpfPattern = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/
const cnpjPattern = /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/

export function resolveAnalyticsEmailDomain(emailValue: string | null | undefined) {
  const email = emailValue?.trim().toLowerCase()
  if (!email) {
    return undefined
  }

  const emailParts = email.split('@')
  if (emailParts.length !== 2) {
    return undefined
  }

  const domain = emailParts[1]
  if (!domain) {
    return undefined
  }

  const normalizedDomain = domain.replace(/\.$/, '')
  return /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/.test(normalizedDomain)
    ? normalizedDomain
    : undefined
}

function isSensitivePropertyName(propertyName: string) {
  if (safeSensitiveAnalyticsPropertyNameExceptions.includes(propertyName)) {
    return false
  }

  return sensitivePropertyNamePattern.test(propertyName)
}

function isSensitiveStringValue(value: string) {
  const trimmedValue = value.trim()
  return (
    emailPattern.test(trimmedValue) ||
    cpfPattern.test(trimmedValue) ||
    cnpjPattern.test(trimmedValue)
  )
}

function sanitizeStringValue(value: string): string | undefined {
  const trimmedValue = value.trim()
  if (!trimmedValue || isSensitiveStringValue(trimmedValue)) {
    return undefined
  }
  return trimmedValue.length > maximumStringLength
    ? trimmedValue.slice(0, maximumStringLength)
    : trimmedValue
}

function sanitizeNumberValue(value: number): number | undefined {
  return Number.isFinite(value) ? value : undefined
}

function sanitizePropertyValue(value: unknown): AnalyticsPropertyValue | undefined {
  if (value === null) {
    return null
  }

  if (typeof value === 'string') {
    return sanitizeStringValue(value)
  }

  if (typeof value === 'number') {
    return sanitizeNumberValue(value)
  }

  if (typeof value === 'boolean') {
    return value
  }

  if (Array.isArray(value)) {
    const sanitizedItems = value
      .slice(0, maximumArrayItems)
      .map(item => sanitizePropertyValue(item))
      .filter(
        (item): item is Exclude<AnalyticsPropertyValue, AnalyticsPropertyValue[]> =>
          item !== undefined && !Array.isArray(item),
      )

    return sanitizedItems.length ? sanitizedItems : undefined
  }

  return undefined
}

export function sanitizeAnalyticsProperties<EventName extends AnalyticsEventName>(
  eventName: EventName,
  properties?: AnalyticsEventProperties[EventName] | null,
): AnalyticsProperties {
  if (!properties) {
    return {}
  }

  const allowedProperties = new Set<string>(
    analyticsEventAllowedProperties[eventName].map(propertyName => String(propertyName)),
  )
  const sanitizedProperties: AnalyticsProperties = {}

  Object.entries(properties as Record<string, unknown>).forEach(([propertyName, propertyValue]) => {
    if (!allowedProperties.has(propertyName) || isSensitivePropertyName(propertyName)) {
      return
    }

    const sanitizedValue = sanitizePropertyValue(propertyValue)
    if (sanitizedValue === undefined) {
      return
    }

    ;(sanitizedProperties as Record<string, AnalyticsPropertyValue>)[propertyName] = sanitizedValue
  })

  return sanitizedProperties
}

export function isSafeAnalyticsIdentifier(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    Boolean(value.trim()) &&
    !isSensitiveStringValue(value) &&
    !emailPattern.test(value)
  )
}
